
import { useEffect, useState } from "react";
import axios from "axios";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import "./index.css";

// ============================================================
// EXISTING SUPREME ADMIN PAGES
// ============================================================

import Index from "./superAdmin/login";

import Tenant from "./superAdmin/tenant";
import TenantEdit from "./superAdmin/tenantEdit";
import Tenant_Add from "./superAdmin/tenant_Add";
import Tenant_Bootstrap from "./superAdmin/tenant_Bootstrap";

import Admin_menu from "./superAdmin/admin_Menu";
import Admin_Menu_edit from "./superAdmin/admin_Menu_edit";
import Admin_module from "./superAdmin/admin_module";

import SuperAdminUsers_List from "./superAdmin/superAdminUsers_List";
import SuperAdminUsers_Add from "./superAdmin/superAdminUsers_Add";
import SuperAdminUsers_Edit from "./superAdmin/superAdminUsers_Edit";

import MiniDatabase from "./superAdmin/mini_database";

// ============================================================
// API CONFIGURATION
// ============================================================

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com"
).replace(/\/$/, "");

const SESSION_KEY = "infra_v5_supremeadmin";

// ============================================================
// SUPREME ADMIN ROUTE GUARD
// ============================================================

function RequireSupremeAdmin() {
  const location = useLocation();

  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let active = true;

    const verifySession = async () => {
      try {
        const response = await axios.get(
          `${API_BASE}/api/supremeadmin/me`,
          {
            withCredentials: true,
            timeout: 15000,
          }
        );

        if (!active) return;

        const user = response.data?.user;

        if (
          response.data?.success &&
          user?.role === "SUPREME_ADMIN"
        ) {
          sessionStorage.setItem(
            SESSION_KEY,
            JSON.stringify({
              user_id: user.user_id,
              username: user.username,
              role: user.role,
            })
          );

          setAuthorized(true);
        } else {
          sessionStorage.removeItem(SESSION_KEY);
          setAuthorized(false);
        }
      } catch (error) {
        if (!active) return;

        sessionStorage.removeItem(SESSION_KEY);
        setAuthorized(false);
      } finally {
        if (active) {
          setChecking(false);
        }
      }
    };

    verifySession();

    return () => {
      active = false;
    };
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
          <p className="text-sm text-slate-300">
            Verifying Supreme Admin session...
          </p>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <Navigate
        to="/supremeadmin/login"
        replace
        state={{ from: location }}
      />
    );
  }

  return <Outlet />;
}

// ============================================================
// MAIN APPLICATION
// ============================================================

export default function App() {
  return (
    <div className="App">
      <Router>
        <Routes>

          {/* ============================================= */}
          {/* PUBLIC ROUTES                                 */}
          {/* ============================================= */}

          <Route
            path="/"
            element={
              <Navigate
                to="/supremeadmin/login"
                replace
              />
            }
          />

          <Route
            path="/supremeadmin/login"
            element={<Index />}
          />

          {/* ============================================= */}
          {/* PROTECTED SUPREME ADMIN ROUTES                */}
          {/* ============================================= */}

          <Route element={<RequireSupremeAdmin />}>

            {/* Default dashboard */}
            <Route
              path="/supreme-admin/dashboard"
              element={<Admin_menu />}
            />

            {/* Existing menu route */}
            <Route
              path="/supremeadmin/admin_Menu"
              element={<Admin_menu />}
            />

            {/* Tenant management */}
            <Route
              path="/supremeadmin/tenant"
              element={<Tenant />}
            />

            <Route
              path="/supremeadmin/tenantEdit/:tenantId"
              element={<TenantEdit />}
            />

            <Route
              path="/supremeadmin/tenant_Add"
              element={<Tenant_Add />}
            />

            <Route
              path="/supremeadmin/tenant_Bootstrap"
              element={<Tenant_Bootstrap />}
            />

            {/* Admin menu management */}
            <Route
              path="/supremeadmin/admin_Menu_edit/:username"
              element={<Admin_Menu_edit />}
            />

            <Route
              path="/supremeadmin/admin_module/:username"
              element={<Admin_module />}
            />

            {/* Mini database */}
            <Route
              path="/supremeadmin/mini_database"
              element={<MiniDatabase />}
            />

            {/* Supreme Admin user management */}
            <Route
              path="/supremeadmin/supremeAdminUsers_List"
              element={<SuperAdminUsers_List />}
            />

            <Route
              path="/supremeadmin/supremeAdminUsers_Add"
              element={<SuperAdminUsers_Add />}
            />

            <Route
              path="/supremeadmin/supremeAdminUsers_Edit/:id"
              element={<SuperAdminUsers_Edit />}
            />

          </Route>

          {/* ============================================= */}
          {/* UNKNOWN ROUTES                                */}
          {/* ============================================= */}

          <Route
            path="*"
            element={
              <Navigate
                to="/supremeadmin/login"
                replace
              />
            }
          />

        </Routes>
      </Router>
    </div>
  );
}
